import React from "react";
import { useTranslation } from "react-i18next";
import ReactSwitch from "react-switch";
import { useSnapshot } from "valtio";

import {
    designStore,
    generalConfigStore,
    setCategoryDisplay,
    setDesignCategories,
    setIsCategoriesScrollActive,
    setIsOrderTakingCategoryTitleActive,
    setPageOrderTaking,
} from "@store";

import {
    CATEGORY_DISPLAY_MODE_1,
    CATEGORY_DISPLAY_MODE_2,
    CATEGORY_COUNT_MIN,
    CATEGORY_COUNT_MAX,
    CATEGORY_COUNT_DEFAULT,
} from "@constants/index";

import { CategoryDisplayMode2Settings } from "./CategoryDisplayMode2Settings";

import "./index.css";

// id DOM reliant le <label> au champ « nombre de catégories » (a11y, WCAG 1.3.1).
const CATEGORY_COUNT_INPUT_ID = "menu-categories-count-input";

export function Menu(): JSX.Element {
    const { t } = useTranslation();

    const {
        project: { template },
        isOrderTakingCategoryTitleActive,
        designCategories,
    } = useSnapshot(generalConfigStore);
    const { isCategoriesScrollActive, categoryDisplayMode } = useSnapshot(
        designStore
    );
    const [isDesignCategoryAuto, setIsDesignCategoryAuto] = React.useState(
        designCategories === "auto" ? true : false
    );
    const [isFocused, setIsFocused] = React.useState(false);
    // Id stable du libellé de groupe pour relier role=radiogroup au titre
    // (WCAG 1.3.1 : le lecteur d'écran annonce l'intitulé du groupe radio).
    const categoryDisplayModeLabelId = React.useId();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        // Conversion explicite dès la réception : `e.target.value` est une string,
        // on la type en number avant toute comparaison (pas de coercition
        // implicite string >= number). Saisie hors bornes → clamp [MIN, MAX].
        const newValue = Number(e.target.value);

        let clamped: number;
        if (newValue < CATEGORY_COUNT_MIN) {
            clamped = CATEGORY_COUNT_MIN;
        } else if (newValue > CATEGORY_COUNT_MAX) {
            clamped = CATEGORY_COUNT_MAX;
        } else {
            clamped = newValue;
        }

        setDesignCategories(clamped);
        setPageOrderTaking({
            ...(template.pages.ways["orderTaking"] as OrderTakingPageType)
                .orderTakingProduct,
            designCategories: clamped,
        });
    };

    const handleFocus = (): void => {
        setIsFocused(true);
    };

    const handleBlur = (): void => {
        setIsFocused(false);
    };
    return (
        <div
            className="m-2 rounded border d-flex flex-column"
            style={{
                borderColor: "#CECECE",
                height: "536px",
            }}
        >
            <div
                className="text-left px-4 d-flex align-items-center cursor__clz"
                style={{
                    backgroundColor: "#EDEDED",
                    height: "40px",
                    fontSize: "20px",
                }}
            >
                {t("Menu")}
            </div>
            <div
                className="p-4"
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                }}
            >
                <div className="py-2 d-flex flex-rows justify-content-between align-items-center">
                    <div>
                        {t("Category titles")}
                        <div style={{ color: "#B7B7B7", fontSize: "13px" }}>
                            {
                                (template.pages.ways[
                                    "orderTaking"
                                ] as OrderTakingPageType).orderTakingProduct
                                    .categoryTitle
                            }
                        </div>
                    </div>
                    <ReactSwitch
                        className="cgt_icn_categoryTitles"
                        aria-label={t("Category titles")}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        handleDiameter={26}
                        offColor="#f7b4b8"
                        offHandleColor="#E30613"
                        checked={isOrderTakingCategoryTitleActive}
                        onChange={() => {
                            setIsOrderTakingCategoryTitleActive(
                                !isOrderTakingCategoryTitleActive
                            );
                            setPageOrderTaking({
                                ...(template.pages.ways[
                                    "orderTaking"
                                ] as OrderTakingPageType).orderTakingProduct,
                                categoryTitle: !isOrderTakingCategoryTitleActive,
                            });
                        }}
                        onColor="#c2eddd"
                        onHandleColor="#34C38F"
                        width={50}
                        height={20}
                    />
                </div>

                <div className="py-2 d-flex flex-rows justify-content-between align-items-center">
                    <div>
                        {t("Automatic scrolling of the category list")}
                        <div style={{ color: "#B7B7B7", fontSize: "13px" }}>
                            {
                                (template.pages.ways[
                                    "orderTaking"
                                ] as OrderTakingPageType).orderTakingProduct
                                    .categoryTitle
                            }
                        </div>
                    </div>
                    <ReactSwitch
                        className="cgt_icn_automaticScrollingCategoryList"
                        aria-label={t(
                            "Automatic scrolling of the category list"
                        )}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        handleDiameter={26}
                        offColor="#f7b4b8"
                        offHandleColor="#E30613"
                        checked={isCategoriesScrollActive}
                        onChange={() => {
                            setIsCategoriesScrollActive(
                                !isCategoriesScrollActive
                            );
                            setPageOrderTaking({
                                ...(template.pages.ways[
                                    "orderTaking"
                                ] as OrderTakingPageType).orderTakingProduct,
                                isCategoriesScrollActive: !isCategoriesScrollActive,
                            });
                        }}
                        onColor="#c2eddd"
                        onHandleColor="#34C38F"
                        width={50}
                        height={20}
                    />
                </div>
                <div className="py-4">
                    <div>{t("Number of categories to display")}</div>
                    <div className="py-3 d-flex flex-rows justify-content-between align-items-center">
                        <div style={{ marginLeft: "100px" }}>{t("Auto")}</div>

                        <ReactSwitch
                            className="cgt_icn_numberCategoriesDisplay"
                            aria-label={t(
                                "Automatic number of categories to display"
                            )}
                            uncheckedIcon={false}
                            checkedIcon={false}
                            handleDiameter={26}
                            offColor="#f7b4b8"
                            offHandleColor="#E30613"
                            checked={isDesignCategoryAuto}
                            onChange={() => {
                                setIsDesignCategoryAuto(!isDesignCategoryAuto);
                                if (!isDesignCategoryAuto === true) {
                                    setDesignCategories("auto" as string);
                                    setPageOrderTaking({
                                        ...(template.pages.ways[
                                            "orderTaking"
                                        ] as OrderTakingPageType)
                                            .orderTakingProduct,
                                        designCategories: "auto" as string,
                                    });
                                } else {
                                    setDesignCategories(CATEGORY_COUNT_DEFAULT);
                                    setPageOrderTaking({
                                        ...(template.pages.ways[
                                            "orderTaking"
                                        ] as OrderTakingPageType)
                                            .orderTakingProduct,
                                        designCategories: CATEGORY_COUNT_DEFAULT,
                                    });
                                }
                            }}
                            onColor="#c2eddd"
                            onHandleColor="#34C38F"
                            width={50}
                            height={20}
                        />
                    </div>
                    <div className="py-3 d-flex flex-rows justify-content-between align-items-center">
                        <label
                            htmlFor={CATEGORY_COUNT_INPUT_ID}
                            className="mb-0"
                            style={{
                                marginLeft: "100px",
                            }}
                        >
                            {t("Manually")}{" "}
                            <span
                                style={{
                                    color:
                                        isDesignCategoryAuto === false
                                            ? "#B7B7B7"
                                            : "unset",
                                }}
                            >
                                {isDesignCategoryAuto === false
                                    ? ` (Minimum ${CATEGORY_COUNT_MIN} : Maximum ${CATEGORY_COUNT_MAX}) `
                                    : null}
                            </span>
                        </label>

                        <input
                            id={CATEGORY_COUNT_INPUT_ID}
                            data-testid="order-taking-menu-categories-count-input"
                            aria-label={t("Number of categories displayed")}
                            className={`cgt_inp_nombreCategorieManuel form-control input-container ${
                                isFocused ? "focused" : ""
                            }`}
                            type="number"
                            value={
                                designCategories !== "auto"
                                    ? (designCategories as number)
                                    : CATEGORY_COUNT_DEFAULT
                            }
                            onChange={handleChange}
                            style={{ width: "60px" }}
                            max={CATEGORY_COUNT_MAX}
                            min={CATEGORY_COUNT_MIN}
                            disabled={
                                isDesignCategoryAuto === true ? true : false
                            }
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                    </div>
                </div>
                <div className="py-2">
                    <div id={categoryDisplayModeLabelId}>
                        {t("Category display mode")}
                    </div>
                    <div
                        className="py-3 d-flex flex-rows align-items-center"
                        role="radiogroup"
                        aria-labelledby={categoryDisplayModeLabelId}
                    >
                        <label
                            className="d-flex align-items-center mb-0"
                            style={{ marginLeft: "100px", cursor: "pointer" }}
                        >
                            <input
                                type="radio"
                                name="categoryDisplayMode"
                                data-testid="order-taking-menu-category-display-mode-1-radio"
                                checked={
                                    categoryDisplayMode ===
                                    CATEGORY_DISPLAY_MODE_1
                                }
                                onChange={() => {
                                    setCategoryDisplay({
                                        categoryDisplayMode: CATEGORY_DISPLAY_MODE_1,
                                    });
                                    setPageOrderTaking({
                                        ...(template.pages.ways[
                                            "orderTaking"
                                        ] as OrderTakingPageType)
                                            .orderTakingProduct,
                                        categoryDisplayMode: CATEGORY_DISPLAY_MODE_1,
                                    });
                                }}
                                style={{ marginRight: "8px" }}
                            />
                            {t("Mode 1")}
                        </label>
                        <label
                            className="d-flex align-items-center mb-0"
                            style={{ marginLeft: "40px", cursor: "pointer" }}
                        >
                            <input
                                type="radio"
                                name="categoryDisplayMode"
                                data-testid="order-taking-menu-category-display-mode-2-radio"
                                checked={
                                    categoryDisplayMode ===
                                    CATEGORY_DISPLAY_MODE_2
                                }
                                onChange={() => {
                                    setCategoryDisplay({
                                        categoryDisplayMode: CATEGORY_DISPLAY_MODE_2,
                                    });
                                    setPageOrderTaking({
                                        ...(template.pages.ways[
                                            "orderTaking"
                                        ] as OrderTakingPageType)
                                            .orderTakingProduct,
                                        categoryDisplayMode: CATEGORY_DISPLAY_MODE_2,
                                    });
                                }}
                                style={{ marginRight: "8px" }}
                            />
                            {t("Mode 2")}
                        </label>
                    </div>
                </div>
                {categoryDisplayMode === CATEGORY_DISPLAY_MODE_2 && (
                    <CategoryDisplayMode2Settings />
                )}
            </div>
        </div>
    );
}
